"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import ErrorModal from "@/components/shared/ErrorModal";
import { logError } from "@/lib/bugsnag";

interface ShowErrorOptions {
  message: string;
  title?: string;
  /** When provided, a "Try again" button runs this callback (modal closes first). */
  onRetry?: () => void;
  retryLabel?: string;
}

interface HandleErrorOptions {
  /** User-facing message shown in the modal. */
  userMessage: string;
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Non-PII metadata attached to the Bugsnag report. */
  metadata?: Record<string, unknown>;
}

interface ErrorModalContextValue {
  /** Show the error modal (display only — does not log). */
  showError: (options: ShowErrorOptions) => void;
  /** Log to console + Bugsnag AND show the modal. Preferred for catch blocks. */
  handleError: (error: unknown, options: HandleErrorOptions) => void;
  hideError: () => void;
}

const ErrorModalContext = createContext<ErrorModalContextValue | null>(null);

interface ModalState {
  open: boolean;
  message: string;
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

const CLOSED: ModalState = { open: false, message: "" };

export function ErrorModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState>(CLOSED);

  const hideError = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const showError = useCallback((options: ShowErrorOptions) => {
    setState({
      open: true,
      message: options.message,
      title: options.title,
      onRetry: options.onRetry,
      retryLabel: options.retryLabel,
    });
  }, []);

  const handleError = useCallback(
    (error: unknown, options: HandleErrorOptions) => {
      logError(error, options.metadata);
      showError({
        message: options.userMessage,
        title: options.title,
        onRetry: options.onRetry,
        retryLabel: options.retryLabel,
      });
    },
    [showError]
  );

  // Retry closes the modal first, then runs the caller's callback.
  const handleRetry = state.onRetry
    ? () => {
        const retry = state.onRetry;
        hideError();
        retry?.();
      }
    : undefined;

  return (
    <ErrorModalContext.Provider value={{ showError, handleError, hideError }}>
      {children}
      <ErrorModal
        isOpen={state.open}
        onClose={hideError}
        message={state.message}
        title={state.title}
        onRetry={handleRetry}
        retryLabel={state.retryLabel}
      />
    </ErrorModalContext.Provider>
  );
}

export function useErrorModal(): ErrorModalContextValue {
  const ctx = useContext(ErrorModalContext);
  if (!ctx) {
    throw new Error("useErrorModal must be used within an ErrorModalProvider");
  }
  return ctx;
}
